import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import type {
  CommandAttempt,
  PriceByType,
  PriceComparisonFilter,
  ProductPriceRow,
  ProductStockRow,
  RetailPriceDiscrepancy,
  WarehouseStock,
} from '../model/types'
import {
  decodeDiscrepancies,
  decodeFilter,
  decodePriceList,
  decodePriceMap,
  decodePriceRecordOutcome,
  decodePriceType,
  decodePriceTypeList,
  decodeProductPriceRows,
  decodeProductStockRows,
  decodeWarehouseStocks,
} from './contracts'
import { decodeEnvelope, requestEnvelope, withMeta, type NativeRead } from './envelope'
import { ONEC_PRICING_URLS } from './urls'
import type { PriceList, PriceListArgs } from '../model/priceList'
import type { PriceType, PriceTypeList } from '../model/priceTypes'

/**
 * Endpoints цен и остатков 1С в ОБЩЕМ `baseApi` (R04): имена `onec*`, rid —
 * аргумент каждого запроса (значит, часть ключа кэша) и часть каждого тега.
 * Ответ сперва проверяется декодером в transport, затем `transformResponse`
 * добавляет то, что знает только мета ответа (свежесть, корреляция).
 */
export const ONEC_PRICING_TAG = 'OnecPricing' as const

export interface ProductArgs {
  resourceId: string
  /** Ключ товара в базе 1С. */
  refKey: string
}

export interface BulkArgs {
  resourceId: string
  refKeys: readonly string[]
  /** Ревизия настроек базы; сменилась — другой ключ кэша и другой запрос. */
  configurationVersion: string
}

export interface DiscrepancyArgs {
  resourceId: string
  /** Пара видов цен; обе пусты — сервер берёт личный фильтр пользователя. */
  first: string | null
  second: string | null
}

export interface ResourceArgs {
  resourceId: string
}

export interface SaveFilterArgs extends CommandAttempt {
  resourceId: string
  configurationVersion: string
  first: string
  second: string
}

export interface CreatePriceRecordArgs {
  resourceId: string
  productKey: string
  priceTypeKey: string
  price: number
  /** Календарная дата `YYYY-MM-DD`; `null` — сегодня. */
  period: string | null
}

export interface DeletePriceRecordArgs {
  resourceId: string
  productKey: string
  priceTypeKey: string
  /** Дата записи из прайса (`YYYY-MM-DD` или `YYYY-MM-DDT00:00:00`). */
  period: string
}

export interface PriceTypeWriteArgs {
  resourceId: string
  name: string
  includesVat: boolean
  comment: string | null
}

export interface PriceTypeUpdateArgs extends PriceTypeWriteArgs {
  typeKey: string
  inactive: boolean
}

export interface PriceTypeMarkArgs {
  resourceId: string
  typeKey: string
  deletionMark: boolean
}

type Wire<T> = { data: T; configurationVersion: string }

/** Ответ после декодера транспорта → ответ для экрана. Тип обещает декодер endpoint-а. */
function finish<T>(response: unknown, meta: unknown): NativeRead<T> {
  return withMeta(response as Wire<T>, meta)
}

const tag = (resourceId: string, id: string) => resourceTag(ONEC_PRICING_TAG, resourceId, id)

export const pricingApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_PRICING_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    /** Остатки товара по складам (живое чтение; свежесть — в заголовках). */
    onecProductStock: build.query<NativeRead<readonly WarehouseStock[]>, ProductArgs>({
      query: ({ resourceId, refKey }) => ({
        url: ONEC_PRICING_URLS.productStock(resourceId, refKey),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeWarehouseStocks),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly WarehouseStock[]>(response, meta),
      providesTags: (_result, _error, { resourceId, refKey }) => [
        tag(resourceId, `STOCK:${refKey}`),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Цены товара по видам цен. */
    onecProductPrices: build.query<NativeRead<readonly PriceByType[]>, ProductArgs>({
      query: ({ resourceId, refKey }) => ({
        url: ONEC_PRICING_URLS.productPrices(resourceId, refKey),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceMap),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly PriceByType[]>(response, meta),
      providesTags: (_result, _error, { resourceId, refKey }) => [
        tag(resourceId, `PRICES:${refKey}`),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Остатки по набору товаров. POST, но чтение: право `stock.read`, эффекта нет. */
    onecStockByRefKeys: build.query<NativeRead<readonly ProductStockRow[]>, BulkArgs>({
      query: ({ resourceId, refKeys, configurationVersion }) => ({
        url: ONEC_PRICING_URLS.stockByRefKeys(resourceId),
        method: 'POST',
        body: requestEnvelope({ lane: 'Catalog', action: 'ProductsStockByRefKeys', configurationVersion }, { refKeys }),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeProductStockRows),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly ProductStockRow[]>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'STOCK:BULK'), resourceScopeTag(resourceId)],
    }),
    /** Цены по набору товаров. */
    onecPricesByRefKeys: build.query<NativeRead<readonly ProductPriceRow[]>, BulkArgs>({
      query: ({ resourceId, refKeys, configurationVersion }) => ({
        url: ONEC_PRICING_URLS.pricesByRefKeys(resourceId),
        method: 'POST',
        body: requestEnvelope({ lane: 'Catalog', action: 'ProductsPricesByRefKeys', configurationVersion }, { refKeys }),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeProductPriceRows),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly ProductPriceRow[]>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICES:BULK'), resourceScopeTag(resourceId)],
    }),
    /** Расхождения розничных цен между двумя видами цен. */
    onecDiscrepancies: build.query<NativeRead<readonly RetailPriceDiscrepancy[]>, DiscrepancyArgs>({
      query: ({ resourceId, first, second }) => ({
        url: ONEC_PRICING_URLS.discrepancies(resourceId),
        params: { first, second },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeDiscrepancies),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly RetailPriceDiscrepancy[]>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [
        tag(resourceId, 'DISCREPANCIES'),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Прайс по виду цен (RP-FB10): страница среза; новых запросов в 1С на страницу нет. */
    onecPriceList: build.query<NativeRead<PriceList>, PriceListArgs>({
      query: ({ resourceId, priceType, query, page, pageSize }) => ({
        url: ONEC_PRICING_URLS.priceList(resourceId),
        params: {
          ...(priceType === null ? {} : { priceType }),
          ...(query === '' ? {} : { q: query }),
          page,
          pageSize,
        },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceList),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceList>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-LIST'), resourceScopeTag(resourceId)],
    }),
    /** RP-FB39: виды цен базы — справочник живьём. */
    onecPriceTypes: build.query<NativeRead<PriceTypeList>, ResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_PRICING_URLS.priceTypes(resourceId),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceTypeList),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceTypeList>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-TYPES'), resourceScopeTag(resourceId)],
    }),
    /** Новый вид цен (валюта, тип и округление — от образца в базе). Один POST без повторов. */
    onecCreatePriceType: build.mutation<NativeRead<PriceType>, PriceTypeWriteArgs>({
      query: ({ resourceId, name, includesVat, comment }) => ({
        url: ONEC_PRICING_URLS.priceTypes(resourceId),
        method: 'POST',
        body: { name, includesVat, comment },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceType),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceType>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-TYPES'), tag(resourceId, 'PRICE-LIST')],
    }),
    onecUpdatePriceType: build.mutation<NativeRead<PriceType>, PriceTypeUpdateArgs>({
      query: ({ resourceId, typeKey, name, includesVat, comment, inactive }) => ({
        url: ONEC_PRICING_URLS.priceType(resourceId, typeKey),
        method: 'PUT',
        body: { name, includesVat, comment, inactive },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceType),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceType>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-TYPES'), tag(resourceId, 'PRICE-LIST')],
    }),
    /** Пометка удаления вида цен или её снятие — как в 1С. */
    onecMarkPriceType: build.mutation<NativeRead<PriceType>, PriceTypeMarkArgs>({
      query: ({ resourceId, typeKey, deletionMark }) => ({
        url: ONEC_PRICING_URLS.priceTypeMark(resourceId, typeKey),
        method: 'PUT',
        body: { deletionMark },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceType),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceType>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-TYPES'), tag(resourceId, 'PRICE-LIST')],
    }),
    /** Создать запись цены в регистре цен 1С (как «Установка цен»). Повтор запрещён: исход неизвестен — только чтением. */
    onecCreatePriceRecord: build.mutation<NativeRead<'applied' | 'unchanged'>, CreatePriceRecordArgs>({
      query: ({ resourceId, productKey, priceTypeKey, price, period }) => ({
        url: ONEC_PRICING_URLS.priceRecords(resourceId),
        method: 'POST',
        body: { productKey, priceTypeKey, price, period },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceRecordOutcome),
      }),
      transformResponse: (response: unknown, meta) => finish<'applied' | 'unchanged'>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-LIST')],
    }),
    /** Удалить запись цены из регистра цен 1С по ключу (период, вид цен, товар). */
    onecDeletePriceRecord: build.mutation<NativeRead<'applied' | 'unchanged'>, DeletePriceRecordArgs>({
      query: ({ resourceId, productKey, priceTypeKey, period }) => ({
        url: ONEC_PRICING_URLS.priceRecords(resourceId),
        method: 'DELETE',
        params: { productKey, priceTypeKey, period },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodePriceRecordOutcome),
      }),
      transformResponse: (response: unknown, meta) => finish<'applied' | 'unchanged'>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'PRICE-LIST')],
    }),
    /** Личный фильтр сравнения цен. Не задан — 404 `price-comparison-filter-not-set`. */
    onecPriceFilter: build.query<NativeRead<PriceComparisonFilter>, ResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_PRICING_URLS.comparisonFilter(resourceId),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeFilter),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceComparisonFilter>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [tag(resourceId, 'FILTER'), resourceScopeTag(resourceId)],
    }),
    onecSavePriceFilter: build.mutation<NativeRead<PriceComparisonFilter>, SaveFilterArgs>({
      query: ({ resourceId, configurationVersion, operationId, idempotencyKey, first, second }) => ({
        url: ONEC_PRICING_URLS.comparisonFilter(resourceId),
        method: 'PUT',
        body: requestEnvelope(
          {
            lane: 'Catalog',
            action: 'SavePriceComparisonFilter',
            configurationVersion,
            command: { operationId, idempotencyKey },
          },
          { first, second },
        ),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeFilter),
      }),
      transformResponse: (response: unknown, meta) => finish<PriceComparisonFilter>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [
        tag(resourceId, 'FILTER'),
        tag(resourceId, 'DISCREPANCIES'),
      ],
    }),
  }),
})

export const {
  onecProductStock,
  onecProductPrices,
  onecStockByRefKeys,
  onecPricesByRefKeys,
  onecDiscrepancies,
  onecPriceFilter,
  onecSavePriceFilter,
  onecPriceList,
  onecCreatePriceRecord,
  onecDeletePriceRecord,
  onecPriceTypes,
  onecCreatePriceType,
  onecUpdatePriceType,
  onecMarkPriceType,
} = pricingApi.endpoints
